import { useEffect, useMemo, useRef, useState } from 'react';
import type { Product } from '../lib/mockData';
import { ProductCard } from './ProductCard';
import { useLocale } from '../context/LocaleContext';

interface ProductCandidatesGridProps {
  products: Product[];
  onProductSelect?: (p: Product) => void;
  initialMobile?: number;  // default 2 (1 row x 2 cols)
  initialTablet?: number;  // default 3 (1 row x 3 cols)
  initialDesktop?: number; // default 4 (1 row x 4 cols)
  stepMobile?: number;
  stepTablet?: number;
  stepDesktop?: number;
  // Pagination props
  hasMore?: boolean;
  isLoadingMore?: boolean;
  totalCount?: number;
  onLoadMore?: () => void;
  desktopCols?: 3 | 4;
  paginationMode?: 'button' | 'infinite';
}

function useResponsiveStep(
  initialMobile = 2, initialTablet = 3, initialDesktop = 4,
  stepMobile = initialMobile, stepTablet = initialTablet, stepDesktop = initialDesktop
) {
  const [cfg, setCfg] = useState({ initial: initialMobile, step: stepMobile });
  useEffect(() => {
    const md = window.matchMedia('(min-width: 768px)');
    const lg = window.matchMedia('(min-width: 1024px)');
    const update = () => {
      if (lg.matches) setCfg({ initial: initialDesktop, step: stepDesktop });
      else if (md.matches) setCfg({ initial: initialTablet, step: stepTablet });
      else setCfg({ initial: initialMobile, step: stepMobile });
    };
    update();
    md.addEventListener('change', update);
    lg.addEventListener('change', update);
    return () => {
      md.removeEventListener('change', update);
      lg.removeEventListener('change', update);
    };
  }, [initialMobile, initialTablet, initialDesktop, stepMobile, stepTablet, stepDesktop]);
  return cfg;
}

function useResponsiveColumns(desktopCols: 3 | 4) {
  const [cols, setCols] = useState(2);
  useEffect(() => {
    const md = window.matchMedia('(min-width: 768px)');
    const lg = window.matchMedia('(min-width: 1024px)');
    const update = () => {
      if (lg.matches) setCols(desktopCols);
      else if (md.matches) setCols(3);
      else setCols(2);
    };
    update();
    md.addEventListener('change', update);
    lg.addEventListener('change', update);
    return () => {
      md.removeEventListener('change', update);
      lg.removeEventListener('change', update);
    };
  }, [desktopCols]);
  return cols;
}

export function ProductCandidatesGrid({
  products,
  onProductSelect,
  initialMobile,
  initialTablet,
  initialDesktop,
  stepMobile,
  stepTablet,
  stepDesktop,
  hasMore = false,
  isLoadingMore = false,
  totalCount,
  onLoadMore,
  desktopCols = 4,
  paginationMode = 'button',
}: ProductCandidatesGridProps) {
  const { t } = useLocale();
  const { initial, step } = useResponsiveStep(initialMobile, initialTablet, initialDesktop, stepMobile, stepTablet, stepDesktop);
  const columns = useResponsiveColumns(desktopCols);
  const [visible, setVisible] = useState(initial);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const prefetchTriggerRef = useRef<HTMLDivElement | null>(null);
  
  // Track initial batch size when products first appear
  const initialBatchSizeRef = useRef<number | null>(null);
  const hasLoggedRenderRef = useRef(false);
  
  // Initialize or reset initial batch size
  useEffect(() => {
    if (products.length > 0) {
      // New batch: reset if products decreased (new search) or first time
      if (initialBatchSizeRef.current === null || products.length < initialBatchSizeRef.current) {
        initialBatchSizeRef.current = products.length;
        setVisible(initial);
        hasLoggedRenderRef.current = false;
      }
    } else {
      // Products cleared: reset tracking
      initialBatchSizeRef.current = null;
      setVisible(initial);
      hasLoggedRenderRef.current = false;
    }
  }, [products.length, initial]);

  // Keep initial visible count in sync with responsive breakpoints.
  // This prevents getting stuck at a smaller first batch (e.g. 4 on desktop).
  useEffect(() => {
    if (products.length === 0) return;
    const minVisible = Math.min(initial, products.length);
    setVisible((prev) => (prev < minVisible ? minVisible : prev));
  }, [initial, products.length]);

  // Auto-expand when new products arrive (after initial batch is fully shown)
  useEffect(() => {
    if (onLoadMore && initialBatchSizeRef.current !== null && products.length > initialBatchSizeRef.current) {
      const initialBatchSize = initialBatchSizeRef.current;
      // Only auto-expand if we've already shown all initial products
      if (visible >= initialBatchSize) {
        setVisible(prev => Math.min(products.length, prev + step));
      }
    }
  }, [products.length, onLoadMore, visible, step]);

  // Always slice products based on visible count
  const items = useMemo(() => {
    return products.slice(0, Math.min(visible, products.length));
  }, [products, visible]);

  // Calculate remaining: total results - currently displayed products
  const remaining = useMemo(() => {
    const displayedCount = items.length;
    const total = typeof totalCount === 'number' ? totalCount : products.length;
    return Math.max(0, total - displayedCount);
  }, [totalCount, products.length, items.length]);
  const hasLoadCapability = remaining > 0 || (onLoadMore && hasMore);
  const prefetchStartIndex = useMemo(() => {
    if (items.length === 0) return -1;
    return Math.max(0, items.length - (columns * 2));
  }, [items.length, columns]);

  // Handle "Visa mer" click
  const handleLoadMore = () => {
    if (onLoadMore && initialBatchSizeRef.current !== null) {
      const initialBatchSize = initialBatchSizeRef.current;
      if (visible < initialBatchSize) {
        // Expand to show all initial products
        setVisible(initialBatchSize);
      } else {
        // Fetch new products
        onLoadMore();
      }
    } else {
      // Legacy behavior
      setVisible(prev => Math.min(products.length, prev + step));
    }
  };

  useEffect(() => {
    if (paginationMode !== 'infinite') return;
    if (!prefetchTriggerRef.current) return;
    if (!hasLoadCapability || isLoadingMore) return;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!rootRef.current || rootRef.current.getClientRects().length === 0) return;
        if (isLoadingMore) return;

        handleLoadMore();
      },
      { root: null, rootMargin: '0px 0px 280px 0px', threshold: 0.01 }
    );

    observer.observe(prefetchTriggerRef.current);
    return () => observer.disconnect();
  }, [paginationMode, hasLoadCapability, isLoadingMore, handleLoadMore, products.length, visible, step, columns]);

  // Track previously rendered count to animate only newly appended items
  const prevVisibleRef = useRef(0);
  const prevVisible = prevVisibleRef.current;
  useEffect(() => { prevVisibleRef.current = items.length; }, [items.length]);

  useEffect(() => {
    if (items.length > 0 && !hasLoggedRenderRef.current) {
      hasLoggedRenderRef.current = true;
      try {
        performance.mark('products-rendered');
        performance.measure(
          'candidates-to-products-render',
          'candidates-received',
          'products-rendered'
        );
        const entries = performance.getEntriesByName('candidates-to-products-render');
        const last = entries[entries.length - 1];
        if (last) {
          console.log('[perf]', last.name, Math.round(last.duration), 'ms');
        }
      } catch {}
    }
  }, [items.length]);

  return (
    <div className="" style={{ fontFamily: 'var(--font-body)' }} ref={rootRef}>
      <div
        className={`grid grid-cols-2 md:grid-cols-3 ${desktopCols === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}
        style={{ gap: 'var(--product-grid-gap)' }}
      >
        {items.map((p, idx) => {
          const isNew = idx >= prevVisible;
          return (
            <div
              key={p.id}
              className={isNew ? 'ww-appear' : ''}
              ref={paginationMode === 'infinite' && idx === prefetchStartIndex ? prefetchTriggerRef : undefined}
            >
              <ProductCard product={p} onProductSelect={onProductSelect} fullWidth />
            </div>
          );
        })}
      </div>

      {paginationMode === 'button' && hasLoadCapability && (
        <div className="flex justify-center mt-3">
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="ww-btn ww-btn-secondary"
          >
            {isLoadingMore ? (
              <>
                <span className="dot-spinner mr-2" />
                {t("common.loading")}
              </>
            ) : (
              `${t("browser.show_more")}${remaining > 0 ? ` (${remaining})` : ''}`
            )}
          </button>
        </div>
      )}
      {paginationMode === 'infinite' && isLoadingMore && (
        <div className="flex justify-center mt-3 text-[12px]" aria-live="polite">
          <span className="dot-spinner mr-2" />
          {t("common.loading")}
        </div>
      )}
    </div>
  );
}


